import { useId } from "@reach/auto-id";
import useEmblaCarousel from "embla-carousel-react";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  type CarouselContextProps,
  type CarouselHandle,
  type CarouselProps,
  DotPagination,
  Flex,
  IconButton,
  type IconButtonProps,
  MsButton,
  getThemeValue,
} from "@theme/components";

import { useEmblaSelector } from "@theme/components/primitives/Carousel/hooks";

import { onKeyDown } from "@theme/utils";

import type { DotPaginationProps } from "@theme/components/Pagination/DotPagination";
import type {
  MsComponentPropsWithRef,
  MsComponentPropsWithoutRef,
  RequireField,
} from "@theme/types/global";
import type { FC, KeyboardEvent } from "react";

const CarouselContext = createContext<CarouselContextProps | null>(null);

const useCarousel = () => {
  const context = useContext(CarouselContext);

  if (!context) throw new Error("useCarousel must be used within a <Carousel />");

  return context;
};

const CarouselA11yComponents: FC = ({ children }) => {
  const { t } = useTranslation("common-phrases");
  const { carouselName } = useCarousel();

  const skipCarouselRef = useRef<HTMLButtonElement>(null);
  const returnToCarouselRef = useRef<HTMLButtonElement>(null);

  const commonProps = {
    type: "button",
    variant: "skipCarouselButton",
    sx: { position: "absolute" },
  } as const;

  return (
    <>
      <MsButton
        {...commonProps}
        ref={skipCarouselRef}
        data-testid="skip-carousel-btn"
        aria-label={`Skip ${carouselName} carousel`}
        onClick={() => returnToCarouselRef.current?.focus()}
      >
        {t("skipCarousel", { defaultValue: "Skip Carousel" })}
      </MsButton>
      {children}
      <MsButton
        {...commonProps}
        ref={returnToCarouselRef}
        data-testid="return-to-carousel-btn"
        aria-label={`End of carousel, return to start of ${carouselName} carousel`}
        sx={{ top: "unset", bottom: 0, ...commonProps.sx }}
        onClick={() => skipCarouselRef.current?.focus()}
      >
        {t("returnToCarousel", { defaultValue: "Return to Carousel" })}
      </MsButton>
    </>
  );
};
CarouselA11yComponents.displayName = "CarouselA11yComponents";

const Carousel = forwardRef<
  CarouselHandle,
  MsComponentPropsWithRef<"div"> & CarouselProps & { removeSkipCarouselButtons?: boolean }
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      children,
      sx,
      "data-testid": dataTestId,
      carouselName,
      onSlideChange,
      removeSkipCarouselButtons = false,
      ...props
    },
    ref,
  ) => {
    const id = useId();
    const carouselId = dataTestId ?? `carousel-component-${id}`;

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins,
    );
    const canScrollPrev = useEmblaSelector(
      api,
      (selectorApi) => selectorApi.canScrollPrev(),
      false,
    );
    const canScrollNext = useEmblaSelector(
      api,
      (selectorApi) => selectorApi.canScrollNext(),
      false,
    );

    // Navigation functions
    const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);
    const scrollNext = useCallback(() => api?.scrollNext(), [api]);
    const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api]);

    // Expose imperative API to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        scrollPrev,
        scrollNext,
        scrollTo,
      }),
      [scrollPrev, scrollNext, scrollTo],
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown(event, scrollPrev, ["ArrowLeft"]);
      onKeyDown(event, scrollNext, ["ArrowRight"]);
    };

    // Expose slide change to parent
    useEffect(() => {
      if (!api || !onSlideChange) return;

      const handleSelect = () => {
        const index = api.selectedScrollSnap();
        onSlideChange(index);
      };

      handleSelect();

      api.on("select", handleSelect);
      api.on("reInit", handleSelect);

      return () => {
        api.off("select", handleSelect);
        api.off("reInit", handleSelect);
      };
    }, [api, onSlideChange]);

    useEffect(() => {
      if (!api || !setApi) return;

      setApi(api);
    }, [api, setApi]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api,
          opts,
          orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          carouselName,
        }}
      >
        <Flex
          onKeyDownCapture={handleKeyDown}
          data-testid={carouselId}
          role="region"
          aria-roledescription="carousel"
          data-slot="carousel"
          sx={{
            position: "relative",
            flexDirection: "column",
            width: "full",
            ...sx,
          }}
          {...props}
          aria-label={carouselName}
        >
          {removeSkipCarouselButtons ? (
            children
          ) : (
            <CarouselA11yComponents>{children}</CarouselA11yComponents>
          )}
        </Flex>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const CarouselContent: FC<
  MsComponentPropsWithoutRef<"div"> & {
    showOverflow?: boolean;
    showLoadingAnimation?: boolean; // We show a loading fade in animation after carousel initializes. This can be turned off but may introduce CLS
    alignment?: "start" | "center"; // Some carousels should be left aligned initially. This property will only be visible before initialization (useful for carousels that display one slide like buy box gallery)
  }
> = ({ sx, showOverflow = false, alignment = "center", showLoadingAnimation = true, ...props }) => {
  const { carouselRef, orientation, api } = useCarousel();
  const initialized = Boolean(api);

  const active = api?.canScrollNext() || api?.canScrollPrev();

  const alignmentStyle = {
    start: "flex-start",
    center: "center",
  };

  const carouselAlignment = alignmentStyle[alignment];

  const isLoading = showLoadingAnimation && !initialized;

  return (
    <Box
      ref={carouselRef}
      data-slot="carousel-content"
      sx={{
        position: "relative",
        width: "full",
        cursor: active ? "grab" : "default",
        overflow: showOverflow ? "visible" : "hidden",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        "&:active": {
          cursor: active ? "grabbing" : "default",
        },
      }}
    >
      <Flex
        sx={{
          flexDirection: orientation === "horizontal" ? "row" : "column",
          justifyContent: !initialized ? carouselAlignment : "unset",
          touchAction: "pan-y",
          willChange: "transform",
          opacity: isLoading ? 0 : 1,
          transition: "opacity .3s ease-in-out",
          ...sx,
        }}
        {...props}
      />
    </Box>
  );
};
CarouselContent.displayName = "CarouselContent";

const CarouselItem: FC<MsComponentPropsWithoutRef<"div">> = ({ sx, ...props }) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const { api } = useCarousel();

  useEffect(() => {
    const el = itemRef.current;
    if (!el || !api || typeof ResizeObserver === "undefined") return;

    let rafId: number | null = null;
    const ro = new ResizeObserver(() => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => api.reInit());
    });

    ro.observe(el);

    return () => {
      ro.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [api]);

  return (
    <Box
      ref={itemRef}
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      sx={{
        flex: "0 0 auto",
        height: "auto",
        minWidth: 0,
        willChange: "transform",
        ":focus-visible": {
          outline: "2px solid",
          outlineColor: "goldPrimary",
          outlineOffset: "-2px",
        },
        ...sx,
      }}
      {...props}
    />
  );
};
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious: FC<IconButtonProps> = ({ ...props }) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <IconButton
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      data-slot="carousel-previous"
      {...props}
    />
  );
};
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext: FC<IconButtonProps> = ({ ...props }) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <IconButton
      disabled={!canScrollNext}
      onClick={scrollNext}
      data-slot="carousel-next"
      {...props}
    />
  );
};
CarouselNext.displayName = "CarouselNext";

const CarouselDotButton: FC<
  RequireField<MsComponentPropsWithoutRef<"button">, "aria-label"> & { index: number }
> = ({ index, ...props }) => {
  const { api } = useCarousel();
  const selectedIndex = useEmblaSelector(api, (selectorApi) => selectorApi.selectedScrollSnap(), 0);
  const isActive = selectedIndex === index;

  const scrollTo = useCallback(
    (ind?: number) => {
      if (api && ind !== undefined) {
        api.scrollTo(ind);
      }
    },
    [api],
  );

  return (
    <IconButton onClick={() => scrollTo(index)} sx={{ width: 20, height: 20, p: "0" }} {...props}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "full",
          bg: isActive ? "goldPrimary" : "transparent",
          border: (t) =>
            `1px solid ${getThemeValue(t, isActive ? "colors.goldPrimary" : "colors.grey300")}`,
        }}
      />
    </IconButton>
  );
};
CarouselDotButton.displayName = "CarouselDotButton";

const CarouselDotNavigation: FC<MsComponentPropsWithoutRef<"ul">> = ({ sx }) => {
  const { api } = useCarousel();
  const snapList = useEmblaSelector(api, (selectorApi) => selectorApi.scrollSnapList(), []);

  if (snapList.length <= 1) return null;

  return (
    <Flex
      as="ul"
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        flexDirection: "row",
        flexWrap: "nowrap",
        alignSelf: "center",
        mt: "s400",
        ...sx,
      }}
    >
      {snapList.map((_, index) => (
        <li key={index}>
          <CarouselDotButton index={index} aria-label={`Go to slide ${index + 1}`} />
        </li>
      ))}
    </Flex>
  );
};
CarouselDotNavigation.displayName = "CarouselDotNavigation";

const CarouselPagination: FC<
  MsComponentPropsWithoutRef<"div"> & Pick<DotPaginationProps, "color">
> = ({ color }) => {
  const { api } = useCarousel();
  const selectedIndex = useEmblaSelector(api, (selectorApi) => selectorApi.selectedScrollSnap(), 0);
  const scrollSnaps = useEmblaSelector(api, (selectorApi) => selectorApi.scrollSnapList(), []);

  if (scrollSnaps.length <= 1) return null;

  return (
    <DotPagination
      currentIndex={selectedIndex}
      totalDots={scrollSnaps.length}
      onPreviousClick={() => api?.scrollPrev()}
      onNextClick={() => api?.scrollNext()}
      color={color}
      ariaLabelPrevious="Previous slide"
      ariaLabelNext="Next slide"
      pageLabel="View"
    />
  );
};
CarouselPagination.displayName = "CarouselPagination";

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDotButton,
  CarouselDotNavigation,
  CarouselPagination,
};
